import { useQuery } from '@tanstack/react-query';

import type { SubscriberPreference } from '~/interfaces/subscriber_preferences';

export const subscriberPreferencesKeys = {
  all: ['subscriber-preferences'] as const,
  bySubscriber: (subscriberId: string) => ['subscriber-preferences', subscriberId] as const,
};

const fetchSubscriberPreferences = async (subscriberId: string): Promise<SubscriberPreference[] | null> => {
  const response = await fetch(`/subscriber_preferences?subscriber_id=${subscriberId}`);
  if (!response.ok) throw new Error('Failed to fetch subscriber preferences');
  return response.json();
};

interface Props {
  enabled: boolean;
  subscriberId: string;
}

interface ReturnProps {
  isLoading: boolean;
  data?: SubscriberPreference[] | null;
  enabled?: boolean;
  refetch: () => void;
}

const useStaticSubscriptionPreferences = ({ enabled, subscriberId }: Props): ReturnProps => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: subscriberPreferencesKeys.bySubscriber(subscriberId),
    queryFn: () => fetchSubscriberPreferences(subscriberId),
    enabled: enabled && !!subscriberId,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isLoading && enabled,
    data: data ?? null,
    refetch,
  };
};

export default useStaticSubscriptionPreferences;
